import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";
import "./CustomerDrillDown.css";
import { local_url } from "../../Const";

export const CustomerDetail = () => {
  const { customerCode, fromDate: initialFromDate, toDate: initialToDate, branchId = 'ALL' } = useParams();
  const navigate = useNavigate();

  // Helper to format date for inputs
  const today = new Date();

  // Function to convert YYYYMMDD to YYYY-MM-DD
  const convertDateFormat = (dateString) => {
    if (dateString && dateString.length === 8) {
      return `${dateString.substring(0, 4)}-${dateString.substring(4, 6)}-${dateString.substring(6, 8)}`;
    }
    return dateString;
  };

  // Format date for input field
  const formatDateForInput = (date) => {
    if (!date) return new Date().toISOString().split("T")[0];

    // If date is in YYYYMMDD format, convert it first
    if (typeof date === 'string' && date.length === 8) {
      return convertDateFormat(date);
    }

    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) {
      return new Date().toISOString().split("T")[0];
    }

    return parsedDate.toISOString().split("T")[0];
  };

  // State initialization using URL parameters with proper formatting
  const [customerDetails, setCustomerDetails] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState(formatDateForInput(initialFromDate));
  const [toDate, setToDate] = useState(formatDateForInput(initialToDate));
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  // Effect to update dates when URL parameters change
  useEffect(() => {
    if (initialFromDate) {
      setFromDate(formatDateForInput(initialFromDate));
    }
    if (initialToDate) {
      setToDate(formatDateForInput(initialToDate));
    }
  }, [initialFromDate, initialToDate]);

  // Helper to format date for API (YYYY-MM-DD to YYYYMMDD)
  const formatDateForAPI = (date) => date.replace(/-/g, "");

  // API URL generator - updated to include branchId
  const getApiUrl = () => {
    return `${local_url}/api/CreditAgeReport/getCreditAgeDetail/${branchId}/${customerCode}/${formatDateForAPI(fromDate)}/${formatDateForAPI(toDate)}`;
  };

  // Extract bill number from saleID (now showing full 6 digits)
  const extractBillNumber = (saleID) => {
    if (!saleID || saleID === "N/A") return "N/A";
    // Extract last 6 digits from saleID
    return saleID.slice(-6);
  };

  // Fetch customer details
  const fetchCustomerDetails = async () => {
    // Retrieve the authorization token from local storage
    const token = localStorage.getItem('AuthorizationToken');
    
    // Check if token exists
    if (!token) {
      setError("Authorization token is missing. Please log in again.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(getApiUrl(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Handle different types of error responses
        if (response.status === 401) {
          throw new Error("Unauthorized. Please log in again.");
        } else if (response.status === 403) {
          throw new Error("Forbidden. You do not have permission to access this resource.");
        } else {
          throw new Error("Network response was not ok");
        }
      }

      const data = await response.json();

      // Process data before sorting
      const processedData = data.map(item => {
        // Extract bill number for sorting (using the full 6 digits now)
        const billNo = item.saleID ? item.saleID.slice(-6) : "000000";
        const dueSinceDate = item.dueSince ? new Date(item.dueSince) : new Date(0);
        return {
          ...item,
          billNoForSort: billNo,
          dueSinceForSort: dueSinceDate
        };
      });

      // Sort data first by Bill No then by Date
      const sortedData = processedData.sort((a, b) => {
        // First sort by bill number
        if (a.billNoForSort !== b.billNoForSort) {
          return a.billNoForSort.localeCompare(b.billNoForSort);
        }
        // Then sort by date
        return a.dueSinceForSort - b.dueSinceForSort;
      });

      setCustomerDetails(sortedData);
      
      // Set customer info if available
      if (sortedData.length > 0) {
        setCustomerInfo({
          name: sortedData[0].customerName,
          creditLimit: sortedData[0].creditLimit,
          creditDays: sortedData[0].creditDays
        });
      }
    } catch (error) {
      setError(error.message || "Failed to fetch customer details. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Handle date changes for Material UI DatePicker
  const handleFromDateChange = (newDate) => {
    if (newDate) {
      const formattedDate = newDate.format('YYYY-MM-DD');
      setFromDate(formattedDate);
      setIsSubmitDisabled(false);
    }
  };

  const handleToDateChange = (newDate) => {
    if (newDate) {
      const formattedDate = newDate.format('YYYY-MM-DD');
      setToDate(formattedDate);
      setIsSubmitDisabled(false);
    }
  };

  // Handle submit - updated to include branchId
  const handleSubmit = () => {
    if (new Date(fromDate) > new Date(toDate)) {
      setError("From date cannot be later than To date.");
      return;
    }

    setError(null);
    setIsSubmitDisabled(true);

    // Update URL with formatted dates for API and branchId
    navigate(`/customer-detail/${customerCode}/${formatDateForAPI(fromDate)}/${formatDateForAPI(toDate)}/${branchId}`);

    // Fetch details for the new date range
    fetchCustomerDetails();
  };

  // Format due date
  const formatDueSinceDate = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Effect to fetch data on initial load
  useEffect(() => {
    fetchCustomerDetails();
  }, [customerCode, branchId]); // Fetch on customerCode or branchId change

  // Calculate totals for summary
  const calculateTotals = () => {
    return customerDetails.reduce((totals, detail) => {
      totals.saleQty += detail.saleQty || 0;
      totals.saleReturnQty += detail.saleReturnQty || 0;
      totals.saleAmount += detail.saleAmount || 0;
      totals.cnReceipt += detail.cnReceipt || 0;
      totals.receipt += detail.receipt || 0;
      return totals;
    }, {
      saleQty: 0,
      saleReturnQty: 0,
      saleAmount: 0,
      cnReceipt: 0,
      receipt: 0
    });
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      "Bill No",
      "Customer Name",
      "Credit Limit",
      "Credit Days",
      "Bill Date",
      "Exceed Days",
      "Sale Quantity",
      "Sale Return Quantity",
      "Sale Amount",
      "CN Receipt",
      "Receivable",
      "Payment Receive",
      "Balance Amount",
    ];

    const rows = customerDetails.map((detail) => {
      const receivable = detail.saleAmount - detail.cnReceipt;
      const billNumber = extractBillNumber(detail.saleID);
      return [
        billNumber,
        detail.customerName,
        detail.creditLimit.toFixed(2),
        detail.creditDays,
        formatDueSinceDate(detail.dueSince),
        detail.exceedDays,
        detail.saleQty,
        detail.saleReturnQty,
        detail.saleAmount.toFixed(2),
        detail.cnReceipt.toFixed(2),
        receivable.toFixed(2),
        detail.receipt.toFixed(2),
        detail.balanceAmount.toFixed(2),
      ];
    });

    let csvContent = "data:text/csv;charset=utf-8,"
      + headers.join(",") + "\n"
      + rows.map((row) => row.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${customerCode}_details.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totals = calculateTotals();
  const totalReceivable = totals.saleAmount - totals.cnReceipt;

  return (
    <div className="customer-aging-detail-container">
      <div className="customer-aging-fixed-header">
        <div className="customer-aging-detail-header">
          <Link to="/AgeingLedger" className="customer-aging-back-button">
            <ArrowBackIcon /> Back to Ledger
          </Link>
          <h1>{customerInfo ? customerInfo.name : "Customer Details"}</h1>
          
          <div className="customer-aging-date-inputs">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={dayjs(fromDate)}
                onChange={handleFromDateChange}
                format="DD-MM-YYYY"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: 150,
                      backgroundColor: '#F1F8E9',
                      '& .MuiOutlinedInput-root': {
                        height: 40,
                      }
                    },
                  },
                }}
              />
            </LocalizationProvider>
            
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To Date"
                value={dayjs(toDate)}
                onChange={handleToDateChange}
                format="DD-MM-YYYY"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: 150,
                      backgroundColor: '#F1F8E9',
                      '& .MuiOutlinedInput-root': {
                        height: 40,
                      }
                    },
                  },
                }}
              />
            </LocalizationProvider>

            <button
              onClick={handleSubmit}
              className="customer-aging-submit-button"
              disabled={loading || isSubmitDisabled}
            >
              Submit
            </button>
            <button
              onClick={exportToCSV}
              className="customer-aging-export-button"
              disabled={customerDetails.length === 0}
            >
              Export to CSV
            </button>
          </div>
        </div>
        
        {error && <div className="customer-aging-error-message">{error}</div>}

        <div className="customer-aging-info-row">
          <strong>Change the Date Range to Get the Particular Bill Number when Bill Number is showing NA</strong>
        </div>
      </div>

      <div className="customer-aging-content">
        {loading ? (
          <div className="customer-aging-loading-container">
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className="customer-aging-table-container">
              <table className="customer-aging-table">
                <thead className="fixed-table-header">
                  <tr>
                    <th>Bill No</th>
                    <th>Bill Date</th>
                    <th>Exceed Days</th>
                    <th>Sale Quantity</th>
                    <th>Sale Return Quantity</th>
                    <th>Sale Amount</th>
                    <th>CN Receipt</th>
                    <th>Receivable</th>
                    <th>Payment Receive</th>
                    <th>Balance Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {customerDetails.length > 0 ? (
                    customerDetails.map((detail, index) => {
                      const isBillNumberEmpty = !detail.saleID || detail.saleID === "N/A";
                      const billNumber = isBillNumberEmpty ? "N/A" : extractBillNumber(detail.saleID);
                      const billDate = isBillNumberEmpty ? "N/A" : formatDueSinceDate(detail.dueSince);
                      const exceedDays = isBillNumberEmpty ? "N/A" : detail.exceedDays;

                      const receivable = detail.saleAmount - detail.cnReceipt;
                      return (
                        <tr key={index}>
                          <td>{billNumber}</td>
                          <td>{billDate}</td>
                          <td>{exceedDays}</td>
                          <td>{detail.saleQty}</td>
                          <td>{detail.saleReturnQty}</td>
                          <td>₹{detail.saleAmount.toFixed(2)}</td>
                          <td>₹{detail.cnReceipt.toFixed(2)}</td>
                          <td>₹{receivable.toFixed(2)}</td>
                          <td>₹{detail.receipt.toFixed(2)}</td>
                          <td>₹{detail.balanceAmount.toFixed(2)}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10">No data available</td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3"><strong>Totals</strong></td>
                    <td><strong>{totals.saleQty.toFixed(2)}</strong></td>
                    <td><strong>{totals.saleReturnQty.toFixed(2)}</strong></td>
                    <td><strong>₹{totals.saleAmount.toFixed(2)}</strong></td>
                    <td><strong>₹{totals.cnReceipt.toFixed(2)}</strong></td>
                    <td><strong>₹{totalReceivable.toFixed(2)}</strong></td>
                    <td><strong>₹{totals.receipt.toFixed(2)}</strong></td>
                    <td><strong>₹{(totalReceivable - totals.receipt).toFixed(2)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerDetail;